import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Button, Form, Select, InputNumber, Alert, Typography, Space, Divider, Tag, Spin
} from 'antd';
import {
  LockOutlined, SafetyCertificateOutlined, CreditCardOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';
import { atriumTokens } from '../../../../theme/theme';
import type { Fee, Owner } from '../../types';

import { formatDate } from '../../utils/date';
const { Text } = Typography;
const { Option } = Select;

interface StripePaymentFormProps {
  condoId: string;
  ownerId: string;
  ownerName?: string;
  ownerEmail?: string;
  fees: Fee[];
  owners: Owner[];
  onSuccess: (paymentIntentId: string) => void;
  onError?: (error: string) => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: atriumTokens.ink,
      fontFamily: atriumTokens.fontFamily,
      '::placeholder': { color: atriumTokens.mut2 },
      iconColor: atriumTokens.blue,
    },
    invalid: { color: atriumTokens.red, iconColor: atriumTokens.red },
  },
  hidePostalCode: false,
};

export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  condoId, ownerId, ownerName, ownerEmail, fees, owners, onSuccess, onError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const pendingFees = fees.filter(f => f.ownerId === ownerId && f.status === 'PENDING');

  const handleFeeSelect = (feeId: string) => {
    const fee = fees.find(f => f.id === feeId);
    if (fee) form.setFieldValue('amount', fee.amount);
  };

  const handleSubmit = async (values: any) => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setLoading(true);
    setCardError(null);

    try {
      // Step 1: Create PaymentIntent on our server — returns only clientSecret
      const idempotencyKey = `card_${condoId}_${ownerId}_${Date.now()}`;
      const { clientSecret } = await apiService.createPaymentIntent({
        condoId,
        ownerId,
        feeId: values.feeId,
        amount: Math.round(values.amount * 100), // cents
        currency: 'cad',
        idempotencyKey,
      });

      // Step 2: Confirm payment on Stripe's servers — card data NEVER touches our backend
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: ownerName || values.cardholderName,
            email: ownerEmail,
          },
        },
      });

      if (result.error) {
        setCardError(result.error.message || 'Payment failed');
        onError?.(result.error.message || 'Payment failed');
      } else if (result.paymentIntent?.status === 'succeeded') {
        form.resetFields();
        cardElement.clear();
        onSuccess(result.paymentIntent.id);
      }
    } catch (err: any) {
      const msg = err.message || 'Payment processing failed';
      setCardError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Security badges */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20,
        padding: '12px 16px',
        background: atriumTokens.blueS2,
        borderRadius: atriumTokens.radiusControl, border: `1px solid ${atriumTokens.line}`
      }}>
        <SafetyCertificateOutlined style={{ color: atriumTokens.ok, fontSize: 20 }} />
        <div>
          <Text strong style={{ color: atriumTokens.ink, fontSize: 13 }}>
            Bank-level Security
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Card data is processed directly by Stripe — your card details never touch our servers. 256-bit TLS encryption.
          </Text>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Tag color="blue" style={{ borderRadius: 4 }}>PCI DSS</Tag>
          <Tag color="green" style={{ borderRadius: 4 }}>3D Secure</Tag>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {pendingFees.length > 0 && (
          <Form.Item name="feeId" label="Apply to Fee (Optional)">
            <Select placeholder="Select a fee to pay" allowClear onChange={handleFeeSelect}>
              {pendingFees.map(fee => (
                <Option key={fee.id} value={fee.id}>
                  {fee.description} — <strong>${fee.amount.toLocaleString()}</strong>
                  {fee.dueDate && <Text type="secondary"> (due {formatDate(fee.dueDate)})</Text>}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="amount"
          label="Payment Amount (CAD)"
          rules={[
            { required: true, message: 'Please enter the payment amount' },
            { type: 'number', min: 1, message: 'Minimum payment is $1.00' },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            step={0.01}
            precision={2}
            formatter={val => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            placeholder="0.00"
            size="large"
          />
        </Form.Item>

        <Form.Item label="Card Details" required>
          <div style={{
            border: `1px solid ${atriumTokens.line2}`,
            borderRadius: atriumTokens.radiusControl,
            padding: '12px 14px',
            background: atriumTokens.card,
            transition: 'border-color 0.3s',
          }}>
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={e => {
                setCardComplete(e.complete);
                setCardError(e.error?.message || null);
              }}
            />
          </div>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            <LockOutlined /> Your card number is encrypted and processed securely by Stripe
          </Text>
        </Form.Item>

        {cardError && (
          <Alert
            type="error"
            message={cardError}
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        <Divider style={{ margin: '16px 0' }} />

        <Form.Item noStyle shouldUpdate>
          {() => {
            const amount = form.getFieldValue('amount');
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {amount && (
                    <Text>
                      Total: <Text strong style={{ fontSize: 18, color: atriumTokens.blue }}>
                        ${Number(amount).toLocaleString('en-CA', { minimumFractionDigits: 2 })} CAD
                      </Text>
                    </Text>
                  )}
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={loading ? <Spin size="small" /> : <CreditCardOutlined />}
                  loading={loading}
                  disabled={!stripe || !cardComplete || loading}
                  size="large"
                  style={{
                    background: atriumTokens.gradHero,
                    border: 'none',
                    borderRadius: atriumTokens.radiusControl,
                    height: 44,
                    paddingInline: 32,
                    fontWeight: 700,
                  }}
                >
                  {loading ? 'Processing...' : 'Pay Now'}
                </Button>
              </div>
            );
          }}
        </Form.Item>
      </Form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Space size={16}>
          <img src="https://js.stripe.com/v3/fingerprinted/img/powered_by_stripe-471fc80599c9f28c5a03.svg"
               alt="Powered by Stripe" style={{ height: 24, opacity: 0.7 }} />
        </Space>
      </div>
    </div>
  );
};
